/**
 * Cron expression → short human label. Shared by the Tasks page and the
 * Workflows › Insights trigger column ("Schedule · daily 02:00").
 */
const CRON_PRESETS: Record<string, string> = {
  "*/5 * * * *": "Every 5 minutes",
  "*/15 * * * *": "Every 15 minutes",
  "*/30 * * * *": "Every 30 minutes",
  "0 * * * *": "Hourly",
  "0 */2 * * *": "Every 2 hours",
  "0 */6 * * *": "Every 6 hours",
  "0 0 * * *": "Daily at midnight",
  "0 9 * * *": "Daily at 09:00",
  "0 9 * * 1-5": "Weekdays at 09:00",
  "0 17 * * 1-5": "Weekdays at 17:00",
  "0 0 * * 1": "Weekly (Monday)",
  "0 0 1 * *": "Monthly (1st)",
};

const DOW_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function cronToText(expr: string | null | unknown): string {
  if (!expr) return "—";
  // Guard against object values stored before normalisation fix
  if (typeof expr === "object") {
    const obj = expr as Record<string, unknown>;
    const val = obj.expression ?? obj.cron ?? obj.schedule ?? obj.value ?? obj.every;
    if (typeof val === "string") return CRON_PRESETS[val] ?? val;
    return "—";
  }
  if (typeof expr !== "string") return "—";
  if (expr === "[object Object]") return "—";
  if (CRON_PRESETS[expr]) return CRON_PRESETS[expr];
  // Try to parse common patterns
  const parts = expr.split(" ");
  if (parts.length === 5) {
    const [min, hr, dom, mon, dow] = parts;
    if (dom === "*" && mon === "*" && min !== "*" && hr !== "*" && /^\d+$/.test(hr!) && /^\d+$/.test(min!)) {
      const time = `${hr!.padStart(2, "0")}:${min!.padStart(2, "0")}`;
      if (dow === "*" || dow === "0-6") return `Daily at ${time}`;
      if (dow === "1-5") return `Weekdays at ${time}`;
      if (/^\d$/.test(dow!)) return `${DOW_NAMES[Number(dow)] ?? dow} at ${time}`;
    }
  }
  return expr;
}

/**
 * Compact variant for dense table cells: "daily 02:00", "weekdays 09:00",
 * "Mon 09:00", "every 15 min".
 */
export function cronToShortText(expr: string | null | unknown): string {
  const long = cronToText(expr);
  return long
    .replace(/^Daily at /, "daily ")
    .replace(/^Weekdays at /, "weekdays ")
    .replace(/^Every (\d+) minutes$/, "every $1 min")
    .replace(/^Every (\d+) hours$/, "every $1h")
    .replace(/^Hourly$/, "hourly")
    .replace(/^Weekly \(Monday\)$/, "Mon 00:00")
    .replace(/^Monthly \(1st\)$/, "monthly")
    .replace(/^Daily at midnight$/, "daily 00:00")
    .replace(/ at /, " ");
}
